/**
 * Préférences utilisateur persistées en localStorage.
 *
 * On évite ici le middleware `persist` de Zustand (qui complique la composition
 * avec immer + devtools déjà en place) en faisant une persistance manuelle :
 *   - lecture initiale depuis localStorage au moment du `create`
 *   - écriture sur chaque mutation via `subscribeWithSelector`
 *
 * Garde-fou : si la lecture/écriture échoue (mode incognito strict, quota
 * dépassé…), on retombe simplement sur les valeurs par défaut sans casser le
 * reste du store.
 */

import type { StateCreator } from 'zustand'

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Comportement du zoom de la timeline agenda.
 *
 *   - `center` : conserve la position visible courante (l'heure au centre de
 *                l'écran reste sur place après changement de zoom).
 *   - `now`    : recentre automatiquement sur l'heure actuelle.
 */
export type ZoomAnchor = 'center' | 'now'

export const ZOOM_ANCHOR_LABELS: Record<ZoomAnchor, string> = {
  center: 'Centré sur la position visible',
  now:    "Recentrer sur l'heure actuelle",
}

export const ZOOM_ANCHOR_HELP: Record<ZoomAnchor, string> = {
  center: "Le zoom préserve l'endroit où vous regardez. Idéal pour inspecter un créneau précis sans perdre votre repère.",
  now:    "Le zoom ramène toujours la vue sur l'heure courante. Pratique pour la régie qui suit le direct en temps réel.",
}

// ─── Forme du slice ──────────────────────────────────────────────────────────

export interface PrefsState {
  zoomAnchor: ZoomAnchor
}

export interface PrefsSlice extends PrefsState {
  setZoomAnchor: (anchor: ZoomAnchor) => void
}

// ─── Persistance localStorage ────────────────────────────────────────────────

const STORAGE_KEY = 'trafficplay.prefs.v1'

const DEFAULT_PREFS: PrefsState = {
  zoomAnchor: 'center',
}

function loadPrefs(): PrefsState {
  if (typeof window === 'undefined') return { ...DEFAULT_PREFS }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_PREFS }
    const parsed = JSON.parse(raw) as Partial<PrefsState>
    return {
      ...DEFAULT_PREFS,
      ...parsed,
      // Validation explicite : on n'accepte que les valeurs connues
      zoomAnchor: parsed.zoomAnchor === 'now' || parsed.zoomAnchor === 'center'
        ? parsed.zoomAnchor
        : DEFAULT_PREFS.zoomAnchor,
    }
  } catch {
    return { ...DEFAULT_PREFS }
  }
}

export function persistPrefs(prefs: PrefsState) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    // Silencieux : quota dépassé ou storage désactivé
  }
}

// ─── Slice creator ───────────────────────────────────────────────────────────

export const createPrefsSlice: StateCreator<
  PrefsSlice,
  [['zustand/immer', never]],
  [],
  PrefsSlice
> = (set) => ({
  ...loadPrefs(),

  setZoomAnchor: (anchor) =>
    set((state) => {
      state.zoomAnchor = anchor
    }),
})
